<script setup lang="ts">
import { useFetch, useRuntimeConfig } from '#app'
import { useAuthStore } from '~/layers/cms/app/stores/authStore'
import type { CmsSettings } from '~/layers/cms/app/types/cmsTypes'
import { onMounted, ref } from 'vue'
import CmsText from '~/layers/cms/app/components/cms/CmsText.vue'
import CmsImage from '~/layers/cms/app/components/cms/CmsImage.vue'
import AppButton from './AppButton.vue'
import { getCmsSettings } from '#imports'

const config = useRuntimeConfig()
const { data } = await useFetch<CmsSettings>(`${config.public.apiBase}/api/get-cms-settings`, {
  default: () => ({} as CmsSettings),
  transform: response => (response ?? {}) as CmsSettings,
  getCachedData: (key, nuxtApp) =>
    nuxtApp.isHydrating ? nuxtApp.payload.data[key] : undefined
})
const settings = ref<CmsSettings>(data.value)

const authStore = useAuthStore()
onMounted(async () => {
  if (authStore.authorized) {
    const res = await getCmsSettings()
    if (res.status_ok) {
      settings.value = res.data as CmsSettings
    }
  }
})
</script>

<template>
  <footer class="pt-20 px-xl pb-10 flex-col gap-18">
    <div class="flex flex-col gap-12">
      <NuxtLink
        to="/"
      >
        <NuxtImg
          :src="settings.logo"
          alt="logo"
          loading="eager"
          width="196"
          height="54"
          quality="100"
          class="object-contain w-49 min-w-49"
        />
      </NuxtLink>
      <div class="flex flex-col items-start xl:flex-row justify-between gap-10">
        <div class="flex flex-col gap-10">
          <span class="text-Tekst text-Menu">
            <CmsText
              global
              :cms-data-path="['global', 'component_data', 'footer_text_1']"
            />
          </span>
          <CmsLink
            v-slot="{ link }"
            :cms-data-path="['global', 'component_data', 'footer_link_1']"
            global
          >
            <AppButton
              button-theme="Transparent-Teksty"
              arrow
              :href="link?.href"
              :target="link?.target"
            >
              <span class="link-text">{{ link?.link_text ?? 'Lorem Ipsum' }}</span>
            </AppButton>
          </CmsLink>
        </div>
        <CmsCustomComponentList
          v-slot="{ component_values }"
          :cms-data-path="['global', 'component_data', 'footer_links_list_5']"
          :open-options="true"
          component-name="footer_links_list_5"
          global
          class="min-w-5 min-h-5 flex flex-col gap-6"
        >
          <div
            v-for="(item, index) in component_values"
            :key="JSON.stringify(item)"
            class="flex items-center gap-4"
          >
            <strong class="text-Paragraph text-Tekst font-bold! text-right xl:w-48 shrink-0">
              <CmsText
                global
                :cms-data-path="['global', 'component_data', 'footer_links_list_5', 'component_data', index, 'text']"
              />
            </strong>
            <span class="text-Tekst text-Menu">
              <CmsLink
                global
                :cms-data-path="['global', 'component_data', 'footer_links_list_5', 'component_data', index, 'link']"
              />
            </span>
          </div>
        </CmsCustomComponentList>
        <CmsCustomComponentList
          v-slot="{ component_values }"
          :cms-data-path="['global', 'component_data', 'footer_links_list_4']"
          :open-options="true"
          component-name="footer_links_list_4"
          global
          class="min-w-5 min-h-5 flex flex-col gap-6"
        >
          <span
            v-for="(item, index) in component_values"
            :key="JSON.stringify(item)"
            class="text-Tekst text-Menu"
          >
            <CmsLink
              global
              :cms-data-path="['global', 'component_data', 'footer_links_list_4', 'component_data', index, 'link']"
            />
          </span>
        </CmsCustomComponentList>
        <CmsCustomComponentList
          v-slot="{ component_values }"
          :cms-data-path="['global', 'component_data', 'footer_links_list_3']"
          :open-options="true"
          component-name="footer_links_list_3"
          global
          class="min-w-5 min-h-5 flex flex-col gap-6"
        >
          <span
            v-for="(item, index) in component_values"
            :key="JSON.stringify(item)"
            class="text-Tekst text-Menu"
          >
            <CmsLink
              global
              :cms-data-path="['global', 'component_data', 'footer_links_list_3', 'component_data', index, 'link']"
            />
          </span>
        </CmsCustomComponentList>
        <div class="flex flex-col gap-6">
          <span class="text-Menu text-Tekst">
            <CmsText
              global
              :cms-data-path="['global', 'component_data', 'footer_text_5']"
            />
          </span>
          <CmsCustomComponentList
            v-slot="{ component_values }"
            :cms-data-path="['global', 'component_data', 'footer_links_list_2']"
            :open-options="true"
            component-name="footer_links_list_2"
            global
            class="min-w-5 min-h-5 flex flex-row justify-between gap-4 items-center"
          >
            <CmsLink
              v-for="(item, index) in component_values"
              :key="JSON.stringify(item)"
              v-slot="{ link }"
              global
              :cms-data-path="['global', 'component_data', 'footer_links_list_2', 'component_data', index, 'link']"
            >
              <NuxtLink
                :to="link?.href"
                :target="link?.target"
              >
                <CmsImage
                  global
                  class="object-contain w-6.5 h-6.5"
                  :cms-data-path="['global', 'component_data', 'footer_links_list_2', 'component_data', index, 'image']"
                />
              </NuxtLink>
            </CmsLink>
          </CmsCustomComponentList>
        </div>
      </div>
      <div class="border-t border-Tertiary">
        <div class="border-b border-background-2 py-6 flex flex-col xl:flex-row justify-between gap-6">
          <span class="text-Tekst text-Paragraph">
            <CmsText
              global
              :cms-data-path="['global', 'component_data', 'footer_text_3']"
            />
          </span>
          <CmsCustomComponentList
            v-slot="{ component_values }"
            :cms-data-path="['global', 'component_data', 'footer_links_list_1']"
            :open-options="true"
            component-name="footer_links_list_1"
            global
            class="min-w-5 min-h-5 flex flex-col xl:flex-row gap-4"
          >
            <span
              v-for="(item, index) in component_values"
              :key="JSON.stringify(item)"
              class="text-Tekst text-Paragraph flex flex-row xl:pl-4"
              :class="index != 0 ? 'xl:border-l xl:border-Tekst' : ''"
            >
              <CmsLink
                global
                :cms-data-path="['global', 'component_data', 'footer_links_list_1', 'component_data', index, 'link']"
              />
            </span>
          </CmsCustomComponentList>
          <div>
            <span class="text-Tekst text-Paragraph no-underline!">
              <CmsText
                global
                :cms-data-path="['global', 'component_data', 'footer_text_4']"
              />
            </span>
          </div>
        </div>
        <div class="flex flex-col xl:flex-row justify-between gap-6 py-6">
          <CmsImage
            :cms-data-path="['global', 'component_data', 'footer_img_1']"
            global
          />
          <div class="flex flex-row gap-6 items-end xl:items-center">
            <span class="text-Paragraph text-Tekst">
              <CmsText
                global
                :cms-data-path="['global', 'component_data', 'footer_text_2']"
              />
            </span>
            <CmsImage
              :cms-data-path="['global', 'component_data', 'footer_img_2']"
              global
            />
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>
